import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * RING-TIER-03 · Feature 4 — Ring 4 Welcome page (standalone).
 *
 * This page exists because Tier 1 and Tier 2 use different scoring
 * rubrics, and moving a founder across that boundary without an
 * explanation is the structural cause of the 93-then-No-Go incident.
 * The stage picker lives here (not on the graduation page) so a founder
 * always sees the tone shift explanation before stage selection.
 *
 * Layout constraints (spec verbatim — do not paraphrase):
 *   Block 1 · Header card
 *   Block 2 · Three tiles (minmax(200px, 1fr), #FAFAF7 fill, no icons)
 *   Block 3 · Stage selector — none pre-selected, "Start Ring 4"
 *              disabled until chosen. No number of any kind (AD1).
 *   Block 4 · "What Ring 4 produces" with four deliverables
 *   Footer   · "Start Ring 4" (orange, right), "Back to dashboard"
 *              (text link, left).
 */
export default function RingFourWelcome() {
  const { id } = useParams();
  const nav = useNavigate();
  const [idea, setIdea] = useState(null);
  const [state, setState] = useState(null);
  const [stage, setStage] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [gradR, ideaR] = await Promise.all([
          api.get(`/ideas/${id}/graduation`),
          api.get(`/ideas/${id}`),
        ]);
        if (!alive) return;
        setState(gradR.data);
        setIdea(ideaR.data.idea);
      } catch (e) {
        toast.error("Could not load Ring 4 Welcome.");
      }
    })();
    return () => { alive = false; };
  }, [id]);

  if (!state || !idea) {
    return <div className="p-12 font-mono text-slate-500">Loading…</div>;
  }
  if (!state.tier1_complete) {
    return (
      <div className="mx-auto max-w-3xl p-8" data-testid="ring4-welcome-locked">
        <h1 className="text-2xl font-semibold">Ring 4 is locked</h1>
        <p className="mt-2 text-slate-600">Ring 3 has not cleared yet. Continue the interrogation.</p>
        <button
          className="mt-6 px-4 py-2 rounded bg-slate-900 text-white"
          onClick={() => nav(`/ideas/${id}`)}
          data-testid="ring4-welcome-back-to-chat"
        >
          Back to Ring 3
        </button>
      </div>
    );
  }

  const startRing4 = async () => {
    if (!stage) return;
    setBusy(true);
    try {
      await api.post(`/ideas/${id}/ring4-start`, { stage });
      toast.success("Ring 4 begins.");
      nav(`/ideas/${id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start Ring 4.");
      setBusy(false);
    }
  };

  const tiles = [
    {
      key: "tone",
      title: "The tone changes",
      body: "The first three rings were built to find what breaks. Ring 4 is collaborative. You'll get \"that won't work if X, but it could work if Y\" — not a verdict.",
    },
    {
      key: "no-failing",
      title: "There's no failing",
      body: "No bounce, no restart, no dead end. A weak plan doesn't get rejected, it gets more questions. You keep building until the plan is right.",
    },
    {
      key: "you-answer",
      title: "You answer, not us",
      body: "Ring 4 hands you the question, never the solution. If you can answer it, you understand your own business well enough to defend it in a room.",
    },
  ];

  return (
    <div className="mx-auto max-w-5xl px-6 py-10" data-testid="ring4-welcome-page">
      {/* Block 1 · Header card */}
      <div
        className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8"
        data-testid="ring4-welcome-header-card"
      >
        <div className="mono-caps text-xs text-orange-600">Ring 4 — Growth Architecture &amp; Investor Readiness</div>
        <h1 className="mt-3 text-3xl md:text-4xl font-semibold text-slate-900 leading-tight">
          Walk into an investor meeting and already have every question they'll ask answered, memorized, cold.
        </h1>
        <p className="mt-3 text-slate-700 max-w-3xl">
          Investability as infrastructure — not "is this fundable" but "can you answer for it?"
        </p>
        <p className="mt-4 text-sm text-slate-500 max-w-3xl">
          Rings 1–3 tested whether the idea survives. Ring 4 builds the plan.
        </p>
      </div>

      {/* Block 2 · Three tiles */}
      <div
        className="mt-6 grid gap-4"
        style={{ gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}
        data-testid="ring4-welcome-tiles"
      >
        {tiles.map((t) => (
          <div
            key={t.key}
            className="rounded-xl p-5"
            style={{ background: "#FAFAF7" }}
            data-testid={`ring4-welcome-tile-${t.key}`}
          >
            <h3 className="font-semibold text-slate-900">{t.title}</h3>
            <p className="mt-2 text-sm text-slate-700 leading-relaxed">{t.body}</p>
          </div>
        ))}
      </div>
      <p className="mt-4 text-sm text-slate-500" data-testid="ring4-welcome-carry-forward">
        Ring 4 carries forward your full Rings 1–3 conversation and every document you just earned.
        You won't re-answer anything.
      </p>

      {/* Block 3 · Stage selector — no number anywhere on this screen (AD1) */}
      <div className="mt-8" data-testid="ring4-welcome-stage-selector">
        <div className="mono-caps text-xs text-slate-500 mb-3">Pick your stage</div>
        <div className="grid gap-3 grid-cols-1 md:grid-cols-3">
          {[
            { key: "pre_concept", label: "Pre-concept", note: "Product not built yet. Targets and derivation accepted." },
            { key: "proven_mvp", label: "Proven MVP", note: "Usage in place, revenue not at scale." },
            { key: "proven_revenue", label: "Proven revenue", note: "Actuals required, derivation shown." },
          ].map((s) => (
            <button
              key={s.key}
              type="button"
              disabled={busy}
              onClick={() => setStage(s.key)}
              data-testid={`ring4-welcome-stage-${s.key}`}
              aria-pressed={stage === s.key}
              className={[
                "text-left px-4 py-3 rounded-lg border-2 transition disabled:opacity-50",
                stage === s.key
                  ? "border-orange-500 bg-orange-50 text-orange-900"
                  : "border-slate-200 hover:border-slate-400 bg-white",
              ].join(" ")}
            >
              <div className="font-semibold">{s.label}</div>
              <div className="text-xs text-slate-500 mt-1">{s.note}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Block 4 · What Ring 4 produces */}
      <div className="mt-8" data-testid="ring4-welcome-produces">
        <div className="mono-caps text-xs text-slate-500 mb-3">What Ring 4 produces</div>
        <ul className="grid gap-2 grid-cols-1 md:grid-cols-2 text-sm text-slate-700">
          <li className="rounded-lg border border-slate-200 p-3">Investor One-Pager (WeasyPrint)</li>
          <li className="rounded-lg border border-slate-200 p-3">Shark Panel Report — 10 personas, readiness label, biggest gaps</li>
          <li className="rounded-lg border border-slate-200 p-3">Ring 4 Growth Architecture brief</li>
          <li className="rounded-lg border border-slate-200 p-3">Updated slide deck with Tier 2 additions</li>
        </ul>
        <p className="mt-3 text-sm text-slate-500">Your Ring 3 reports stay as they are.</p>
      </div>

      {/* Footer — orange Start Ring 4 right, Back to dashboard text link left */}
      <div className="mt-10 flex items-center justify-between" data-testid="ring4-welcome-footer">
        <Link
          to="/dashboard"
          data-testid="ring4-welcome-back-link"
          className="text-sm text-slate-600 hover:text-slate-900 underline underline-offset-4"
        >
          ← Back to dashboard
        </Link>
        <button
          type="button"
          onClick={startRing4}
          disabled={!stage || busy}
          data-testid="ring4-welcome-start"
          className="px-5 py-2.5 rounded-lg bg-orange-500 hover:bg-orange-600 text-white font-semibold shadow-sm disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Start Ring 4
        </button>
      </div>
    </div>
  );
}
